import { useState } from "react";
import { useTheme } from "@hooks/useTheme";
import { TickIcon } from "../Icons";
import { useTranslation } from "react-i18next";
import { CHARACTER_LENGTH } from "@constants/index";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const ApproveRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  requestId,
  "data-testid": dataTestId = "approve-request-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({ requestId, message, action: "approve" });
    }
    setMessage("");
    onClose();
  };

  const handleClose = () => {
    setMessage("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[50vh] max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className="px-6 py-6 flex-shrink-0" data-testid={`${dataTestId}-header`}>
          <div className="flex items-center gap-4">
            <div className="relative" data-testid={`${dataTestId}-icon`}>
              <div className="flex h-14 w-14 items-center justify-center">
                <TickIcon className="h-16 w-16" />
              </div>
            </div>

            <h2 className={`${colors.textMessage} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("communication.approve-request-title")}
            </h2>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden" data-testid={`${dataTestId}-form`}>
          <div className="flex-1 overflow-y-auto px-6 space-y-4" data-testid={`${dataTestId}-content`}>
            <p className={`${colors.textMessage} font-normal text-sm`} data-testid={fieldValueTestId("description")}>
              {t("communication.are-you-sure-approve")}
            </p>

            <div data-testid={fieldContainerTestId("message")}>
              <p
                className={`${colors.textMessage} font-normal text-sm mb-1`}
                data-testid={fieldLabelTestId("message")}
              >
                {t("communication.message-optional")}
              </p>
              <textarea
                data-testid={fieldInputTestId("message")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("communication.write-message-optional")}
                maxLength={CHARACTER_LENGTH.REQUEST}
                className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                rows="10"
              />
              <p className={`text-xs ${colors.textMuted} mt-1`} data-testid={fieldValueTestId("message-char-count")}>
                {message.length}/{CHARACTER_LENGTH.REQUEST}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 py-4" data-testid={`${dataTestId}-footer`}>
            <div className={`h-px w-full ${colors.bgDivider} mb-4`}></div>

            <div className={`${colors.borderPrimary} px-6`}>
              <div className="flex items-center justify-end">
                <div className="flex items-center space-x-3">
                  <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>
                    {t("communication.cancel")}
                  </OutlineButton>
                  <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSubmit}>
                    {t("communication.approve")}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApproveRequestModal;
